/*
 * Filename:/src/plugins/cleanupPlugin.ts
 * Created Date: Monday, September 29th 2025, 3:29:29 pm
 * Author: David Phenicie
 *
 * Copyright (c) 2025 Near Earth Autonomy
 */

import { nextTick } from 'vue'

// A WeakMap is used to store component instances without preventing them from being garbage collected.
// This is a robust way to manage cleanup without creating new memory leaks.
const componentCleanupMap = new WeakMap<any, () => void>()

/**
 * This is the master cleanup function that the router will call.
 * It iterates over all active, registered components and triggers their cleanup.
 */
async function cleanupAllComponents() {
  // We don't need to iterate here anymore, as the unmounted hook will handle removal.
  // The primary purpose is now just to introduce the nextTick delay.
  await nextTick()
}

export const cleanupPlugin = {
  install(app) {
    // This mixin will be applied to EVERY component in your application.
    app.mixin({
      data() {
        // Every component now gets its own `isVisible` property, defaulting to true.
        return {
          isVisible: true,
        }
      },
      mounted() {
        // When a component is mounted, we create its specific cleanup function.
        const cleanup = () => {
          this.isVisible = false
        }
        // We store the component instance and its cleanup function in the WeakMap.
        componentCleanupMap.set(this, cleanup)
      },
      async beforeUnmount() {
        // Just before a component is destroyed, we run its cleanup function.
        const cleanup = componentCleanupMap.get(this)
        if (cleanup) {
          cleanup()
          // We wait for the DOM to update (because of isVisible = false) before proceeding.
          await nextTick()
          componentCleanupMap.delete(this)
        }
      },
    })
  },
}
